import { DiscoveryApi, FetchApi } from '@backstage/frontend-plugin-api';
import { BackendAuthApi } from '@internal/plugin-neo4j-auth-common';
import {
  Configuration,
  ConfigurationApi,
  EnvironmentApi,
  EnumEnvironmentOperationStatus,
  type EnumEnvironmentOperationType,
  RegionApi,
  type RequestEnvironment,
  type RequestEnvironmentConfig,
  type RequestScaleUp,
  type ResponseEnvironment,
  type ResponseEnvironmentConfig,
  type ResponseEnvironmentCreated,
  type ResponseEnvironmentList,
  ResponseError,
  type ResponseOpCreated,
  type ResponseRegionList,
  type V3EnvironmentsEnvironmentIDClustersPostRequest,
  type V3EnvironmentsEnvironmentIDComponentsPostRequest,
  type V3EnvironmentsEnvironmentIDOwnersPutRequest,
  type ResponseDiffReport,
} from '@internal/backstage-plugin-neo4j-environment-manager-common';
import { EnvironmentApiError } from './errors';
import type {
  ConfigsApi,
  ConfigsRawJsonCache,
  DispatchOption,
  OmniEnvironmentsApi,
  GetConfigsResult,
  RawEnvironmentBody,
} from './types';

/**
 * REST implementation of the Omni Environments API
 */
export class EnvironmentsRestApi implements OmniEnvironmentsApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;
  private readonly auth: BackendAuthApi;

  constructor(options: {
    discoveryApi: DiscoveryApi;
    fetchApi: FetchApi;
    auth: BackendAuthApi;
  }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
    this.auth = options.auth;
  }

  private async getApiConfig(): Promise<Configuration> {
    const baseUrl = await this.discoveryApi.getBaseUrl('proxy');
    const token = await this.auth.getToken();

    return new Configuration({
      basePath: `${baseUrl}/omni-environments`,
      fetchApi: this.fetchApi.fetch,
      headers: token ? { 'Environments-Token': token } : undefined,
    });
  }

  private async getEnvironmentApi(): Promise<EnvironmentApi> {
    const config = await this.getApiConfig();
    return new EnvironmentApi(config);
  }

  private async getRegionApi(): Promise<RegionApi> {
    const config = await this.getApiConfig();
    return new RegionApi(config);
  }

  async getEnvironment(
    environmentID: string,
    includeStatusHistory = true,
  ): Promise<ResponseEnvironment> {
    try {
      const api = await this.getEnvironmentApi();
      return await api.v3EnvironmentsEnvironmentIDGet({
        environmentID,
        includeStatusHistory,
      });
    } catch (error) {
      return this.handleSdkError(error);
    }
  }

  async getEnvironmentWithRawClusters(environmentID: string): Promise<{
    environment: ResponseEnvironment;
    rawClusterDefinitions: Record<string, unknown>[];
  }> {
    try {
      const api = await this.getEnvironmentApi();
      const response = await api.v3EnvironmentsEnvironmentIDGetRaw({
        environmentID,
      });
      const rawJson = await response.raw.clone().json();
      const environment = await response.value();
      const rawClusters = (rawJson.spec?.clusters ?? []) as Array<{
        definition?: Record<string, unknown>;
        name?: string;
      }>;
      return {
        environment,
        rawClusterDefinitions: rawClusters.map(c => c.definition ?? {}),
      };
    } catch (error) {
      return this.handleSdkError(error);
    }
  }

  async getEnvironmentRawJson(environmentID: string): Promise<unknown> {
    try {
      const api = await this.getEnvironmentApi();
      const response = await api.v3EnvironmentsEnvironmentIDGetRaw({
        environmentID,
      });
      return await response.raw.json();
    } catch (error) {
      return this.handleSdkError(error);
    }
  }

  async getEnvironments(options?: {
    searchTerm?: string;
    includeDeleted?: boolean;
    ownerIdentifier?: string;
  }): Promise<ResponseEnvironmentList> {
    try {
      const api = await this.getEnvironmentApi();
      const result = await api.v3EnvironmentsGet({
        searchTerm: options?.searchTerm,
        includeDeleted: options?.includeDeleted,
        ownerIdentifier: options?.ownerIdentifier,
      });
      return result ?? { items: [] };
    } catch (error) {
      return this.handleSdkError(error);
    }
  }

  async getEnvironmentNames(): Promise<string[]> {
    try {
      const api = await this.getEnvironmentApi();
      const result = await api.v3EnvironmentsNamesGet({});
      return result ?? [];
    } catch (error) {
      return this.handleSdkError(error);
    }
  }

  async createEnvironment(request: {
    environment: RequestEnvironment;
    dispatchOption?: DispatchOption;
  }): Promise<ResponseEnvironmentCreated> {
    try {
      const api = await this.getEnvironmentApi();
      return await api.v3EnvironmentsPost(request);
    } catch (error) {
      return this.handleSdkError(error);
    }
  }

  async deleteEnvironment(request: {
    environmentID: string;
    dispatchOption?: DispatchOption;
  }): Promise<ResponseOpCreated | null | undefined> {
    try {
      const api = await this.getEnvironmentApi();
      return await api.v3EnvironmentsEnvironmentIDDelete(request);
    } catch (error) {
      return this.handleSdkError(error);
    }
  }

  async updateEnvironment(request: {
    environmentID: string;
    dispatchOption?: DispatchOption;
  }): Promise<ResponseOpCreated | null | undefined> {
    try {
      const api = await this.getEnvironmentApi();
      return await api.v3EnvironmentsEnvironmentIDUpdatePost(request);
    } catch (error) {
      return this.handleSdkError(error);
    }
  }

  async addClusters(
    request: V3EnvironmentsEnvironmentIDClustersPostRequest,
  ): Promise<ResponseOpCreated | null | undefined> {
    try {
      const api = await this.getEnvironmentApi();
      return await api.v3EnvironmentsEnvironmentIDClustersPost(request);
    } catch (error) {
      return this.handleSdkError(error);
    }
  }

  async addComponents(
    request: V3EnvironmentsEnvironmentIDComponentsPostRequest,
  ): Promise<ResponseOpCreated | null | undefined> {
    try {
      const api = await this.getEnvironmentApi();
      return await api.v3EnvironmentsEnvironmentIDComponentsPost(request);
    } catch (error) {
      return this.handleSdkError(error);
    }
  }

  async scaleUpEnvironment(request: {
    environmentID: string;
    duration: RequestScaleUp;
  }): Promise<ResponseOpCreated | null | undefined> {
    try {
      const api = await this.getEnvironmentApi();
      return await api.v3EnvironmentsEnvironmentIDScaleUpPost(request);
    } catch (error) {
      return this.handleSdkError(error);
    }
  }

  async scaleDownEnvironment(request: {
    environmentID: string;
  }): Promise<ResponseOpCreated | null | undefined> {
    try {
      const api = await this.getEnvironmentApi();
      return await api.v3EnvironmentsEnvironmentIDScaleDownPost(request);
    } catch (error) {
      return this.handleSdkError(error);
    }
  }

  async cancelOperation(request: {
    environmentID: string;
    operationID: number;
    name: string;
    type: EnumEnvironmentOperationType;
  }): Promise<void> {
    try {
      const api = await this.getEnvironmentApi();
      // name + type are immutable server-side and validated on update, so they
      // must be echoed back; cancelling is just setting the status to CANCELLED.
      await api.v3EnvironmentsEnvironmentIDOperationsOperationIDPut({
        environmentID: request.environmentID,
        operationID: request.operationID,
        owners: {
          name: request.name,
          type: request.type,
          status: EnumEnvironmentOperationStatus.EnvOpCancelled,
        },
      });
    } catch (error) {
      await this.handleSdkError(error);
    }
  }

  async replaceOwners(
    request: V3EnvironmentsEnvironmentIDOwnersPutRequest,
  ): Promise<void> {
    try {
      const api = await this.getEnvironmentApi();
      await api.v3EnvironmentsEnvironmentIDOwnersPut(request);
    } catch (error) {
      await this.handleSdkError(error);
    }
  }

  async putEnvironmentUntyped(
    environmentID: string,
    body: RawEnvironmentBody,
  ): Promise<ResponseDiffReport | null | undefined> {
    try {
      const config = await this.getApiConfig();
      const url = `${config.basePath}/v3/environments/${encodeURIComponent(environmentID)}`;
      const response = await this.fetchApi.fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...config.headers,
        },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        throw new ResponseError(response, 'Response returned an error code');
      }
      if (response.status === 200) {
        return await response.json();
      }
      return null;
    } catch (error) {
      return this.handleSdkError(error);
    }
  }

  async getEnvironmentConfig(
    environmentID: string,
    options?: { stripIdentifiers?: boolean },
  ): Promise<ResponseEnvironmentConfig> {
    try {
      const api = await this.getEnvironmentApi();
      return await api.v3EnvironmentsEnvironmentIDConfigGet({
        environmentID,
        stripIdentifiers: options?.stripIdentifiers,
      });
    } catch (error) {
      return this.handleSdkError(error);
    }
  }

  async getRegions(): Promise<ResponseRegionList> {
    try {
      const api = await this.getRegionApi();
      return await api.v3RegionsGet();
    } catch (error) {
      return this.handleSdkError(error);
    }
  }

  /**
   * Helper function to handle errors from SDK calls
   */
  private async handleSdkError(error: unknown): Promise<never> {
    if (error instanceof ResponseError) {
      const response = error.response;
      let errorMessage = error.message || response.statusText;
      let errorCode: number | undefined;
      let errors: string[] | undefined;

      try {
        const json = await response.json();
        if (json.message) {
          errorMessage = json.message;
        } else if (json.error) {
          errorMessage = json.error;
        } else if (typeof json === 'string') {
          errorMessage = json;
        }

        if (typeof json.code === 'number') {
          errorCode = json.code;
        }

        if (Array.isArray(json.errors)) {
          errors = json.errors.filter(
            (e: unknown): e is string => typeof e === 'string',
          );
        }
      } catch {
        // Failed to parse error response, use default message
      }

      throw new EnvironmentApiError(
        errorMessage,
        response.status,
        errorCode,
        errors,
      );
    }

    if (error instanceof Error) {
      throw new EnvironmentApiError(error.message, 400);
    }

    throw new EnvironmentApiError('Unknown error', 400);
  }
}

/**
 * REST implementation of the Omni Configs API
 */
export class ConfigsRestApi implements ConfigsApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;
  private readonly auth: BackendAuthApi;

  constructor(options: {
    discoveryApi: DiscoveryApi;
    fetchApi: FetchApi;
    auth: BackendAuthApi;
  }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
    this.auth = options.auth;
  }

  private async getApiConfig(): Promise<Configuration> {
    const baseUrl = await this.discoveryApi.getBaseUrl('proxy');
    const token = await this.auth.getToken();

    return new Configuration({
      basePath: `${baseUrl}/omni-environments`,
      fetchApi: this.fetchApi.fetch,
      headers: token ? { 'Environments-Token': token } : undefined,
    });
  }

  private async getConfigsApi(): Promise<ConfigurationApi> {
    const config = await this.getApiConfig();
    return new ConfigurationApi(config);
  }

  async getConfigs(): Promise<GetConfigsResult> {
    try {
      const api = await this.getConfigsApi();
      // Use raw API to access JSON before SDK deserialization
      const rawResponse = await api.v3ConfigsGetRaw();

      // Get both the raw JSON and deserialized response
      // Return them together to avoid race conditions
      const rawJson = (await rawResponse.raw
        .clone()
        .json()) as ConfigsRawJsonCache;
      const configs = (await rawResponse.value()) ?? { items: [] };

      return { configs, rawJson };
    } catch (error) {
      return this.handleSdkError(error);
    }
  }

  async deleteConfig(configName: string): Promise<void> {
    try {
      const api = await this.getConfigsApi();
      return await api.v3ConfigsConfigNameDelete({ configName });
    } catch (error) {
      return this.handleSdkError(error);
    }
  }

  async createConfig(request: {
    configName: string;
    config: RequestEnvironmentConfig;
  }): Promise<void> {
    try {
      const api = await this.getConfigsApi();
      return await api.v3ConfigsConfigNamePut(request);
    } catch (error) {
      return this.handleSdkError(error);
    }
  }

  /**
   * Helper function to handle errors from SDK calls
   */
  private async handleSdkError(error: unknown): Promise<never> {
    if (error instanceof ResponseError) {
      const response = error.response;
      let errorMessage = error.message || response.statusText;
      let errorCode: number | undefined;
      let errors: string[] | undefined;

      try {
        const json = await response.json();
        if (json.message) {
          errorMessage = json.message;
        } else if (json.error) {
          errorMessage = json.error;
        } else if (typeof json === 'string') {
          errorMessage = json;
        }

        if (typeof json.code === 'number') {
          errorCode = json.code;
        }

        if (Array.isArray(json.errors)) {
          errors = json.errors.filter(
            (e: unknown): e is string => typeof e === 'string',
          );
        }
      } catch {
        // Failed to parse error response, use default message
      }

      throw new EnvironmentApiError(
        errorMessage,
        response.status,
        errorCode,
        errors,
      );
    }

    if (error instanceof Error) {
      throw new EnvironmentApiError(error.message, 400);
    }

    throw new EnvironmentApiError('Unknown error', 400);
  }
}
